'use client';
// pricing.css defines the .pricing-plan_* and .calc_* Webflow classes this
// component renders. Co-locating the import here guarantees the styles ship with
// the component on every route that uses it (/pricing AND the dynamically
// imported pricing section on /admissions-consulting/*), instead of relying on a
// global stylesheet. Without it the buttons fell back to the bare Webflow
// `.w-button` blue (#3898ec) and failed the color-contrast audit.
import '@/styles/pricing.css';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@workspace/ui/components/accordion";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { courseConfigs, calculatePrice, getPricingPlansConfig, pricingDetailsConfig } from "@/constant/pricing";
import { isOfferActive, applyDiscount, marketingBannerConfig } from "@/constant/marketingBanner";
import { Button } from "@workspace/ui/components/button";
import { siteSettings } from "@workspace/utils";
import { loadRazorpay } from "@/lib/razorpay";
import Link from "next/link";

/**
 * URL search params, read on the client only.
 *
 * The Next `useSearchParams()` hook opts a statically-generated route into
 * client-side rendering at its nearest Suspense boundary — so the pricing
 * calculator/plans (wrapped in <Suspense>) were prerendered as a tiny fallback
 * and only painted their full height after hydration, shoving the footer down
 * ~1000px (CLS ~1.0 on every /admissions-consulting/* page and /pricing).
 *
 * This hook instead returns an empty URLSearchParams during SSR and the first
 * client render (identical on both → no hydration mismatch, and the calculator
 * falls back to its prop/default values), then the real params after mount.
 * That lets the components server-render at their natural height (zero CLS)
 * while still honouring ?course=/?schools=/?package= deep-links post-hydration.
 */
function useClientSearchParams(): URLSearchParams {
    const [params, setParams] = useState<URLSearchParams>(() => new URLSearchParams());
    useEffect(() => {
        const read = () => setParams(new URLSearchParams(window.location.search));
        read();
        window.addEventListener("popstate", read);
        return () => window.removeEventListener("popstate", read);
    }, []);
    return params;
}

/**
 * Creates a Razorpay order via the backend, then opens Razorpay Checkout.
 *
 * The order is created server-side (apps/api → POST /api/payments/order) so the
 * amount is authoritative and the key secret never reaches the browser. On a
 * successful payment we route to a confirmation state; production should still
 * verify the payment signature server-side before granting access.
 */
async function startRazorpayCheckout({
    amountRupees,
    description,
    router,
}: {
    amountRupees: number;
    description: string;
    router: ReturnType<typeof useRouter>;
}): Promise<void> {
    try {
        const loaded = await loadRazorpay();
        if (!loaded || !window.Razorpay) {
            alert("Unable to load the payment gateway. Please check your connection and try again.");
            return;
        }

        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/payments/order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                amount: Math.round(amountRupees * 100), // paise
                currency: "INR",
                plan: description,
            }),
        });
        if (!res.ok) throw new Error(`Order request failed (${res.status})`);

        const data = await res.json();
        if (!data?.orderId) throw new Error("Order id missing in response");

        const rzp = new window.Razorpay({
            key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
            order_id: data.orderId,
            amount: data.amount,
            currency: data.currency || "INR",
            name: siteSettings.siteName,
            description,
            theme: { color: "#420926" },
            // Razorpay reports success in the browser, but access is granted only
            // after the backend re-computes and matches the payment signature.
            handler: async (response) => {
                try {
                    const verifyRes = await fetch(
                        `${process.env.NEXT_PUBLIC_API_URL}/api/payments/verify`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            }),
                        }
                    );
                    const verifyData = await verifyRes.json();
                    if (verifyRes.ok && verifyData?.verified) {
                        // The backend builds the encoded success URL (and has saved
                        // the order). Fall back to a local one only if it's absent.
                        const successParams = new URLSearchParams({
                            payment_id: response.razorpay_payment_id,
                            order_id: response.razorpay_order_id,
                            amount: String(amountRupees),
                            plan: description,
                        });
                        router.push(
                            verifyData.redirectUrl || `/payment-success?${successParams.toString()}`
                        );
                    } else {
                        alert(
                            "We received your payment but could not verify it. If money was deducted, please contact us and we'll sort it out right away."
                        );
                    }
                } catch (verifyErr) {
                    console.error("Payment verification error:", verifyErr);
                    alert(
                        "We received your payment but could not verify it. If money was deducted, please contact us and we'll sort it out right away."
                    );
                }
            },
            modal: {
                // User dismissed the modal without paying — no charge, no-op.
                ondismiss: () => {
                    console.info("Razorpay checkout dismissed by user.");
                },
            },
        });
        rzp.on("payment.failed", (response) => {
            const err = (response as { error?: { description?: string; reason?: string; code?: string } })?.error;
            const reason = err?.description || err?.reason || err?.code || "Unknown error";
            // Full payload logged so the exact failure (e.g. unsupported method,
            // international card, order/key mismatch) is visible in the console.
            console.error("Razorpay payment.failed:", response);
            alert(`Payment failed: ${reason}. Please try again.`);
        });
        rzp.open();
    } catch (err) {
        console.error("Razorpay checkout error:", err);
        alert("Something went wrong while starting the payment. Please try again.");
    }
}

function CheckoutDisclaimer() {
    return (
        <p
            className="mt-4 text-xs text-gray-500 dark:text-gray-400"
        >
            By proceeding with the payment, you confirm that you have read and agreed to {' '}{siteSettings.siteName}’s{' '}
            <Link href="/legal/terms" className="underline">
                Terms &amp; Conditions
            </Link>
            ,{' '}
            <Link href="/legal/privacy-policy" className="underline">
                Privacy Policy
            </Link>
            , and{' '}
            <Link href="/legal/refund-policy" className="underline">
                Refund &amp; Payment Policy
            </Link>
            . All services are advisory in nature and do not guarantee admissions,
            scholarships, or outcomes. Refunds are subject to eligibility criteria and
            service initiation status. Please review your selected package and school
            count carefully before proceeding. For installment plans, full access to
            services is granted only upon receipt of the final payment.
        </p>
    );
}

type PricingHeaderProps = { title: string; description: string };
export function PricingHeader({ title, description }: PricingHeaderProps) {
    return (
        <section id="pricing-plans" className="section-pricing-header">
            <div className="padding-global">
                <div className="container-large">
                    <div className="pricing-header_component">
                        <div className="max-width-large align-center">
                            <div className="margin-bottom margin-xsmall">
                                <h1 className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)] text-align-center">{title}</h1>
                            </div>
                        </div>
                        <div className="max-width-large align-center">
                            <p
                                className="text-size-medium text-align-center"
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

type CalcPricingPlansProps = {
    course?: string; // Course passed from parent page
    showAllTabs?: boolean; // Whether to show all tabs or hide them
};

export function CalcPricingPlans({ course, showAllTabs = true }: CalcPricingPlansProps) {
    const router = useRouter();
    const searchParams = useClientSearchParams();

    // Use course from props if provided, otherwise from URL, otherwise default to MIM
    const defaultCourse = course || searchParams.get("course") || "MIM";
    const defaultSchools = Number(searchParams.get("schools")) || 4;
    const defaultPackage = Number(searchParams.get("package")) || 0;

    const [selectedCourse, setSelectedCourse] = useState(defaultCourse);
    const [schoolCount, setSchoolCount] = useState(defaultSchools);
    const [selectedPackage, setSelectedPackage] = useState(defaultPackage);

    useEffect(() => {
        // Update from props first, then URL params
        const courseFromProps = course || searchParams.get("course") || "MIM";
        setSelectedCourse(courseFromProps);
        setSchoolCount(Number(searchParams.get("schools")) || 4);
        setSelectedPackage(Number(searchParams.get("package")) || 0);
    }, [course, searchParams, defaultCourse]);

    // Update URL when parameters change
    useEffect(() => {
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set("course", selectedCourse);

        const courseConfig = courseConfigs[selectedCourse as keyof typeof courseConfigs];
        if (courseConfig?.isPackageBased) {
            params.set("package", String(selectedPackage));
            params.delete("schools");
        } else {
            params.set("schools", String(schoolCount));
            params.delete("package");
        }

        router.replace(`?${params.toString()}`, { scroll: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourse, schoolCount, selectedPackage, course]);

    const courseConfig = courseConfigs[selectedCourse as keyof typeof courseConfigs];
    let price = 0;
    let planTitle = "";

    if (courseConfig?.isPackageBased && 'packages' in courseConfig) {
        // MBA package-based pricing
        const selectedPkg = courseConfig.packages[selectedPackage];
        if (selectedPkg) {
            price = selectedPkg.price;
            planTitle = selectedPkg.name;
        }
    } else if (courseConfig && 'schoolPricingTiers' in courseConfig) {
        // Tier-based pricing
        price = calculatePrice(selectedCourse, schoolCount);
        planTitle = schoolCount <= courseConfig.maxRegularTiers
            ? `${schoolCount} School${schoolCount > 1 ? "s" : ""} Plan`
            : `${schoolCount} Schools Plan (includes additional schools)`;
    }

    const updateQueryParams = (pkg: number, schools: number) => {
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set("course", selectedCourse);

        const config = courseConfigs[selectedCourse as keyof typeof courseConfigs];
        if (config?.isPackageBased) {
            params.set("package", String(pkg));
            params.delete("schools");
        } else {
            params.set("schools", String(schools));
            params.delete("package");
        }

        router.replace(`?${params.toString()}`, { scroll: false });
    };

    const handlePackageChange = (value: string) => {
        const pkgIndex = Number(value);
        setSelectedPackage(pkgIndex);
        updateQueryParams(pkgIndex, schoolCount);
    };

    const handleSchoolChange = (value: string) => {
        const count = Number(value);
        setSchoolCount(count);
        updateQueryParams(selectedPackage, count);
    };

    const renderSchoolSelection = () => {
        if (courseConfig?.isPackageBased && 'packages' in courseConfig) {
            return (
                <div className="calc_input">
                    <label className="input_label">Select Package:</label>
                    <Select
                        value={selectedPackage.toString()}
                        onValueChange={handlePackageChange}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select package" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {courseConfig.packages.map((pkg, index) => (
                                    <SelectItem key={index} value={index.toString()}>
                                        {pkg.name}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
            );
        }

        return (
            <div className="calc_input">
                <label className="input_label">Number of schools:</label>
                <Select
                    value={schoolCount.toString()}
                    onValueChange={handleSchoolChange}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select schools" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            {[2, 3, 4, 5, 6, 7, 8].map(n => (
                                <SelectItem key={n} value={n.toString()}>
                                    {n} School{n > 1 ? "s" : ""}
                                </SelectItem>
                            ))}
                            {[...Array(2)].map((_, i) => {
                                const n = i + 9;
                                return (
                                    <SelectItem key={n} value={n.toString()}>
                                        {n} School{n > 1 ? "s" : ""}
                                    </SelectItem>
                                );
                            })}
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
        );
    };
    const handleCheckout = async () => {
        // Charge the price actually shown to the user (discounted during offers).
        const amountRupees = isOfferActive() ? applyDiscount(price) : price;
        if (!amountRupees) return;

        await startRazorpayCheckout({
            amountRupees,
            description: planTitle || `${selectedCourse} admissions package`,
            router,
        });
    };

    return (
        <div className="calc_component">
            {/* Course Selection Tabs - Only show if showAllTabs is true and no specific course is provided */}
            {(showAllTabs && !course) && (
                <div className="course-tabs-container">
                    <div className="course-tabs">
                        {Object.entries(courseConfigs).map(([key, config]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setSelectedCourse(key)}
                                className={`course-tab ${selectedCourse === key ? 'active' : ''}`}
                            >
                                {config.tabName}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div className="calc_form-block w-form bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]">
                <form className="calc_form" onSubmit={e => e.preventDefault()}>
                    <div className="calc_form-header">
                        <h2 className="heading-xxsmall text-color-gray-50 dark:text-gray-50">How much does it cost?</h2>
                    </div>



                    {/* Course Title - Show when specific course is provided */}
                    {course && (
                        <div className="course-title-container">
                            <h3 className="course-title">
                                {courseConfigs[selectedCourse as keyof typeof courseConfigs]?.displayName}
                            </h3>
                        </div>
                    )}

                    <div className="calc_form-content">
                        {renderSchoolSelection()}
                    </div>
                </form>
                <div id="calc-output" className="calc_output">
                    <div className="calc_output-item">
                        <div className="calc_price-title">
                            {planTitle}
                        </div>
                        <div className="calc_price-wrapper">
                            {isOfferActive() ? (
                                <>
                                    <div className="d-flex align-items-baseline gap-2">
                                        <div className="calc_currency text-[var(--secondary--aubergine)] dark:text-[var(--pink--200)]">INR</div>
                                        <div className="calc_price text-[var(--secondary--aubergine)] dark:text-[var(--pink--200)]">
                                            {applyDiscount(price).toLocaleString("en-IN")}
                                        </div>
                                        <div className="calc_price-original">
                                            INR {price.toLocaleString("en-IN")}
                                        </div>
                                    </div>
                                    <div className="offer-badge">
                                        {marketingBannerConfig.discount} — Limited time
                                    </div>
                                </>
                            ) : (
                                <div className="d-flex">
                                    <div className="calc_currency text-[var(--secondary--aubergine)] dark:text-[var(--pink--200)]">INR</div>
                                    <div className="calc_price text-[var(--secondary--aubergine)] dark:text-[var(--pink--200)]">{price.toLocaleString("en-IN")}</div>
                                </div>
                            )}
                            {courseConfig &&
                                !courseConfig.isPackageBased &&
                                'schoolPricingTiers' in courseConfig &&
                                schoolCount > courseConfig.maxRegularTiers && (
                                    <div className="calc_terms text-size-tiny">
                                        Each additional school after {courseConfig.maxRegularTiers}: INR {courseConfig.additionalSchoolPrice.toLocaleString("en-IN")}
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
                <CheckoutDisclaimer />
                <div className="calc_details">
                    <div className="calc_cta">

                        {/* A checkout action, not a navigation target — render a
                            <button> so it has no empty href (Lighthouse
                            crawlable-anchors) and is keyboard/AT operable. */}
                        <button type="button" onClick={() => handleCheckout()} className='button is-fluid w-button'>Purchase Package</button>
                        {pricingDetailsConfig.cta.map((cta, idx) => {
                            const isExternal = /^https?:\/\//.test(cta.href);
                            return (
                                <Link
                                    key={idx}
                                    href={cta.href}
                                    className={cta.className}
                                    {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                >
                                    {cta.text}
                                </Link>
                            );
                        })}
                    </div>
                    <div className="calc_content">
                        <h3 className="text-size-regular">{pricingDetailsConfig.content.title}</h3>
                        {pricingDetailsConfig.content.paragraphs.map((p, idx) => (
                            <p className="text-size-small" key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                        ))}
                    </div>
                    <div className="calc_summary">
                        <Accordion type="single" collapsible>
                            <AccordionItem value="summary">
                                <AccordionTrigger className="calc_summary-header justify-between p-0 hover:no-underline">
                                    <div className="calc_summary-title">
                                        <div className="icon-1x1-small w-embed" dangerouslySetInnerHTML={{ __html: pricingDetailsConfig.summary.icon.svg }} />
                                        <h3 className="text-size-small">{pricingDetailsConfig.summary.title}</h3>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="calc_summary-content">
                                    {pricingDetailsConfig.summary.content.map((c, idx) => (
                                        <div className="text-size-tiny" key={idx} dangerouslySetInnerHTML={{ __html: c }} />
                                    ))}
                                    <div className="padding-bottom padding-xxsmall"></div>
                                    <p className="text-size-tiny">
                                        <Link href={pricingDetailsConfig.summary.contact.href} className="text-style-link">{pricingDetailsConfig.summary.contact.text}</Link>
                                        {pricingDetailsConfig.summary.contact.after}
                                    </p>
                                    <div className="padding-bottom padding-xsmall"></div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>
                </div>
            </div>
        </div>
    );
}

type PricingPlan = {
    name: string;
    description: string;
    cta: { text: string; href: string };
    active?: boolean;
    schools?: number;
    id?: string | number;
    packageIndex?: number;
    popular?: boolean;
};

type PricingPlansProps = {
    plans?: PricingPlan[];
    course?: string; // Course passed from parent page
};

export function PricingPlans({ plans = [], course }: PricingPlansProps) {
    const router = useRouter();
    const searchParams = useClientSearchParams();

    // Initialise from the `course` prop / "MIM" default *during render* (lazy
    // initialisers) so the plan cards are present in the SSR/prerendered HTML.
    // Previously these started empty and were only filled by the post-mount
    // useEffect below, so the cards popped in after hydration and shoved the
    // footer down ~650px (CLS ~0.8 on every /admissions-consulting/* page).
    const [currentCourse, setCurrentCourse] = useState<string>(course || "MIM");
    const [coursePlans, setCoursePlans] = useState<PricingPlan[]>(
        () => (getPricingPlansConfig(course || "MIM").slice(0, 4) || plans) as PricingPlan[]
    );
    const [activePlanName, setActivePlanName] = useState<string | null>(null);

    const courseFromUrl = course || searchParams.get("course") || "MIM";
    const schoolFromUrl = Number(searchParams.get("schools"));
    const packageFromUrl = Number(searchParams.get("package"));

    useEffect(() => {
        // Update from props first, then URL params
        const courseFromProps = course || searchParams.get("course") || "";
        const coursePlans = (getPricingPlansConfig(courseFromProps).splice(0, 4) || plans) as PricingPlan[];


        // const coursePlans = plans.length > 0 ? plans : getPricingPlansConfig(currentCourse);
        const activePlanName = getActivePlanName();
        setCurrentCourse(courseFromProps);
        setCoursePlans(coursePlans);
        setActivePlanName(activePlanName);
        // getActivePlanName reads coursePlans state that this same effect sets,
        // so adding it (or its deps) here would create a re-run loop. The effect
        // intentionally only re-runs on course/URL changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course, searchParams, courseFromUrl]);

    // Use course-specific plans if no plans provided
    // Determine which plan should be active
    const getActivePlanName = () => {
        if (coursePlans) {
            const courseConfig = courseConfigs[courseFromUrl as keyof typeof courseConfigs];

            if (courseConfig?.isPackageBased) {
                const match = coursePlans.find((plan) => plan.packageIndex === packageFromUrl);
                return match ? match.name : null;
            } else if (schoolFromUrl) {
                const match = coursePlans.find((plan) => plan.schools === schoolFromUrl);
                return match ? match.name : null;
            }
        }
        return null;
    };

    const handlePlanSelect = (plan: PricingPlan) => {
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set('course', currentCourse);

        const config = courseConfigs[currentCourse as keyof typeof courseConfigs];
        if (config?.isPackageBased) {
            if (plan.packageIndex !== undefined) {
                params.set('package', String(plan.packageIndex));
            }
            params.delete('schools');
        } else {
            if (plan.schools) {
                params.set('schools', String(plan.schools));
            }
            params.delete('package');
        }

        router.replace(`?${params.toString()}`, { scroll: false });
        setActivePlanName(plan.name);
    };

    const handleCheckout = async (plan: PricingPlan) => {
        const basePrice = calculatePrice(currentCourse, plan.schools ?? 0, plan.packageIndex);
        // Charge the price actually shown to the user (discounted during offers).
        const amountRupees = isOfferActive() ? applyDiscount(basePrice) : basePrice;
        if (!amountRupees) return;

        await startRazorpayCheckout({
            amountRupees,
            description: plan.name?.replace(/<[^>]*>/g, "") || `${currentCourse} admissions package`,
            router,
        });
    };


    return (
        <div className="pricing-plan_component">
            {coursePlans && coursePlans.map((plan: PricingPlan, index: number) => {
                // If parameters from URL match, make that plan active, else use default
                const isActive = activePlanName
                    ? plan.name === activePlanName
                    : !!plan.active;

                return (
                    <div
                        key={`${plan.name}-${index}`}
                        className={`pricing-plan_item mb-1 border-0 bg-[var(--primary--beige)] dark:bg-[var(--gray--100)] is-${plan.name.toLowerCase().replace(/\s+/g, '-')}-plan${isActive ? " is-active-plan" : ""}`}
                        onClick={() => handlePlanSelect(plan)}
                    >
                        <div className="pricing-plan_content">
                            <div className="heading-xxsmall"
                                dangerouslySetInnerHTML={{ __html: plan.name }}></div>

                            {(() => {
                                const planPrice = calculatePrice(currentCourse, plan.schools ?? 0, plan.packageIndex);
                                if (!planPrice) return null;
                                return isOfferActive() ? (
                                    <div className="margin-top margin-custom2">
                                        <div className="pricing-plan_price-row">
                                            <span className="pricing-plan_price-discounted heading-xxsmall text-[var(--secondary--aubergine)] dark:text-[var(--pink--200)]">
                                                INR {applyDiscount(planPrice).toLocaleString("en-IN")}
                                            </span>
                                            <span className="pricing-plan_price-original">
                                                INR {planPrice.toLocaleString("en-IN")}
                                            </span>
                                        </div>
                                        {/* <div
                                            className="text-size-small"

                                        >
                                            INR {applyDiscount(planPrice).toLocaleString("en-IN")}
                                        </div> */}
                                    </div>
                                    // <div className="pricing-plan_price-block">
                                    //     <div className="offer-badge">{marketingBannerConfig.discount}</div>
                                    //     <div className="pricing-plan_price-row">
                                    //         <span className="pricing-plan_price-discounted">
                                    //             INR {applyDiscount(planPrice).toLocaleString("en-IN")}
                                    //         </span>
                                    //         <span className="pricing-plan_price-original">
                                    //             INR {planPrice.toLocaleString("en-IN")}
                                    //         </span>
                                    //     </div>
                                    // </div>
                                ) : (
                                    <div className="margin-top margin-custom2">
                                        <div
                                            className="text-size-small"
                                            dangerouslySetInnerHTML={{ __html: plan.description }}
                                        />
                                    </div>
                                    // <div className="pricing-plan_price-block">
                                    //     <span className="pricing-plan_price-discounted">
                                    //         INR {planPrice.toLocaleString("en-IN")}
                                    //     </span>
                                    // </div>
                                );
                            })()}
                        </div>
                        <div className="pricing-plan_footer">
                            <Button
                                onClick={() => isActive && handleCheckout(plan)}
                                className="pricing-plan_button h-12 border=[1px solid] w-button text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)] hover:text-white"
                            >
                                {isActive ? 'Get Started' : plan.cta.text}
                                {/* {isActive ? 'Get Started' : plan.cta.text} */}
                            </Button>

                        </div>
                        <div className="pricing-plan_background"></div>
                    </div>
                );
            })}
        </div>
    );
}

type PricingUSPProps = {
    usps: {
        // icon: string;
        title: string;
        paragraphs: string[];
    }[];
};

export function PricingUSP({ usps }: PricingUSPProps) {
    return (
        <section className="section-pricing-usp">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-vertical padding-xhuge">
                        <div className="pricing-usp_component">
                            {usps.map((usp, idx) => (
                                <div className="pricing-usp_item bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]" key={idx}>
                                    {/* <div className="pricing-usp_icon-wrapper">
                                        <img src={usp.icon} loading="lazy" alt="" className="pricing-usp_icon" />
                                    </div> */}
                                    <h3 className="heading-xxsmall">{usp.title}</h3>
                                    {usp.paragraphs.map((p, i) => (
                                        <div className={i === 0 ? "margin-top margin-custom2" : "margin-top margin-xsmall"} key={i}>
                                            <p className="text-size-small" dangerouslySetInnerHTML={{ __html: p }} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}